import React from "react";
import { func, shape, string } from "prop-types";
import { ApolloProvider, createHttpLink } from "@apollo/client";
import { ApolloClient } from "@apollo/client/core";
import { InMemoryCache } from "@apollo/client/cache";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

// import resolvers from '@magento/venia-ui/lib/resolvers';
import { shrinkGETQuery } from "@magento/venia-ui/lib/util/shrinkGETQuery";
import { getStoreCodeUrl } from "../../../extensions/store-url-resolver/storeUrlResolver";

/**
 * The counterpart to `@magento/venia-drivers` is an adapter that provides
 * context objects to the driver dependencies. The default implementation in
 * `@magento/venia-drivers` uses modules such as `react-redux`, which
 * have implicit external dependencies. This adapter provides all of them at
 * once.
 *
 * Consumers of Venia components can either implement a similar adapter and
 * wrap their Venia component trees with it, or they can override `src/drivers`
 * so its components don't depend on context and IO.
 *
 * @param {String} props.apiBase base path for url
 * @param {Object} props.apollo.cache an apollo cache instance
 * @param {Object} props.apollo.client an apollo client instance
 * @param {Object} props.apollo.link an apollo link instance
 * @param {Object} props.store redux store to provide
 */
var VeniaAdapter = props => {
  var {
    apiBase,
    apollo = {},
    children,
    store
  } = props;
  var link = apollo.link || VeniaAdapter.apolloLink(apiBase);
  var apolloClient;
  if (apollo.client) {
    apolloClient = apollo.client;
  } else {
    apolloClient = new ApolloClient({
      cache: new InMemoryCache(),
      link
      // resolvers
    });
    apolloClient.apiBase = apiBase;
  }
  var browserRouterProps = {};
  var storeCodeUrl = getStoreCodeUrl();
  console.log("store code language: ", storeCodeUrl);
  browserRouterProps.basename = "/".concat(storeCodeUrl);
  return /*#__PURE__*/React.createElement(ApolloProvider, {
    client: apolloClient
  }, /*#__PURE__*/React.createElement(ReduxProvider, {
    store: store
  }, /*#__PURE__*/React.createElement(BrowserRouter, browserRouterProps, children)));
};

/**
 * We attach this Link as a static method on VeniaAdapter because
 * other modules in the codebase need access to it.
 */
VeniaAdapter.apolloLink = apiBase => {
  // Intercept and shrink URLs from GET queries. Using
  // GET makes it possible to use edge caching in Magento
  // Cloud, but risks exceeding URL limits with default usage
  // of Apollo's http link. `shrinkGETQuery` encodes the URL
  // in a more efficient way.
  var customFetchToShrinkQuery = (uri, options) => {
    var url = uri;
    if (options.method === "GET") {
      url = shrinkGETQuery(uri);
    }
    return fetch(url, options);
  };
  return createHttpLink({
    uri: apiBase,
    fetch: customFetchToShrinkQuery,
    // Warning: useGETForQueries risks exceeding URL length limits. These limits
    // in practice are typically set at or behind where TLS terminates. For Magento
    // Cloud and Fastly, 8kb is the maximum by default
    // https://docs.fastly.com/en/guides/resource-limits#request-and-response-limits
    useGETForQueries: true
  });
};
VeniaAdapter.propTypes = {
  apiBase: string.isRequired,
  apollo: shape({
    client: shape({
      query: func.isRequired
    }),
    link: shape({
      request: func.isRequired
    }),
    cache: shape({
      readQuery: func.isRequired
    })
  }),
  store: shape({
    dispatch: func.isRequired,
    getState: func.isRequired,
    subscribe: func.isRequired
  }).isRequired
};
export default VeniaAdapter;